import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import { BodySmall } from 'styles/typography';

type Props = {
  onCancel: () => void;
  onConfirm: () => void;
  isArchiving: boolean;
};

/**
 * The confirmation in front of archiving a referral (OLIO-2599).
 *
 * Copy is the design's, verbatim. Both lines earn their place: archiving ends the referral for
 * good, and the second line answers the question the first one raises, which is where the
 * referral went.
 *
 * `PrimaryRed` (light header, red title) rather than `Danger` (solid red band), confirmed against
 * the design with UX. It is also what the rest of the app's destructive confirmations use, from
 * deleting a denial list entry to removing a med list, so archiving reads the same as every other
 * "this cannot be undone" moment.
 */
export default function ArchiveReferralModal({ onCancel, onConfirm, isArchiving }: Props) {
  return (
    <StandardModal colorTheme={ColorThemeName.PrimaryRed} onCancel={onCancel} title='Archive Referral?'>
      <Content>
        <Paragraph>
          Are you sure you wish to archive this referral? Once archived, this action cannot be undone.
        </Paragraph>
        <Paragraph>Archived referrals can be accessed on the History tab of the Inbound Referrals page.</Paragraph>
      </Content>
      <StyledButtonGroup>
        <Button variant='ghost' onClick={onCancel} data-testid='cancel-archive'>
          Cancel
        </Button>
        <Button variant='danger' onClick={onConfirm} loading={isArchiving} data-testid='confirm-archive'>
          Archive
        </Button>
      </StyledButtonGroup>
    </StandardModal>
  );
}

const StyledButtonGroup = styled(ButtonGroup)`
  justify-content: flex-end;
`;

const Content = styled.div`
  margin: 26px 0 50px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Paragraph = styled(BodySmall)`
  display: block;
`;
